/**
 * Audio module. Uses HTMLAudio for streamed theme assets and falls back to
 * lightweight Web Audio synths when a URL is missing, blocked, or slow to play.
 */

export const DEFAULT_AUDIO_THEME = Object.freeze({
  // CC0: https://github.com/mdn/webaudio-examples (audio-basics/outfoxing.mp3)
  musicUrl: 'https://mdn.github.io/webaudio-examples/audio-basics/outfoxing.mp3',
  // CC0 Wikimedia Commons pronunciation clips; short and reliable as fallback effects.
  countdownSfxUrl: 'https://upload.wikimedia.org/wikipedia/commons/9/91/LL-Q1860_%28eng%29-Wodencafe-beep.wav',
  betTapSfxUrl: '',
  spinSfxUrl: 'https://upload.wikimedia.org/wikipedia/commons/b/b5/LL-Q1860_%28eng%29-Wodencafe-beeps.wav',
  winSfxUrl: 'https://upload.wikimedia.org/wikipedia/commons/f/fd/LL-Q1860_%28eng%29-Flame%2C_not_lame-game_on.wav',
  loseSfxUrl: 'https://upload.wikimedia.org/wikipedia/commons/7/74/LL-Q1860_%28eng%29-Flame%2C_not_lame-game_over.wav',
});

let ctx = null;
let unlocked = false;
let muted = false;
let bgEl = null;
let bgUrl = null;
let bgVolume = 0.4;
let bgSynth = null;
let activeTheme = { ...DEFAULT_AUDIO_THEME };

const cache = new Map();

export function withDefaultAudioTheme(theme = {}) {
  const merged = { ...DEFAULT_AUDIO_THEME, ...(theme || {}) };
  for (const key of Object.keys(DEFAULT_AUDIO_THEME)) {
    if (!theme || !theme[key]) merged[key] = DEFAULT_AUDIO_THEME[key];
  }
  return merged;
}

export function setAudioTheme(theme = {}) {
  activeTheme = withDefaultAudioTheme(theme);
}

function ensureCtx() {
  if (!ctx) {
    try { ctx = new (window.AudioContext || window.webkitAudioContext)(); } catch (e) {}
  }
  return ctx;
}

export function unlockAudio() {
  if (!unlocked) unlocked = true;
  ensureCtx();
  if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
  resumeBackground();
}

export function setMuted(m) {
  muted = m;
  if (bgEl) bgEl.muted = m;
  if (muted) stopGeneratedBackground();
  else resumeBackground();
}

export function isMuted() { return muted; }

export function getAudioState() {
  return {
    unlocked,
    muted,
    bgUrl,
    bgVolume,
    hasBackgroundElement: !!bgEl,
    backgroundPaused: bgEl ? bgEl.paused : null,
    backgroundReadyState: bgEl ? bgEl.readyState : null,
    backgroundNetworkState: bgEl ? bgEl.networkState : null,
    backgroundCurrentTime: bgEl ? bgEl.currentTime : null,
    usingGeneratedBackground: !!bgSynth,
    defaultMusicUrl: DEFAULT_AUDIO_THEME.musicUrl,
    defaultBetTapSound: DEFAULT_AUDIO_THEME.betTapSfxUrl || 'generated-coin-tap',
  };
}

export function stopAllAudio() {
  muted = true;
  stopGeneratedBackground();
  whooshStop();
  if (bgEl) {
    try { bgEl.pause(); bgEl.currentTime = 0; } catch (e) {}
  }
  for (const el of cache.values()) {
    try {
      el.pause();
      el.currentTime = 0;
      el.onended = null;
      el.onerror = null;
    } catch (e) {}
  }
  bgEl = null;
  bgUrl = null;
  cache.clear();
  if (ctx) {
    try { ctx.close().catch(() => {}); } catch (e) {}
    ctx = null;
  }
  unlocked = false;
}

function getEl(url) {
  if (!url) return null;
  if (cache.has(url)) return cache.get(url);
  const el = new Audio(url);
  el.preload = 'auto';
  cache.set(url, el);
  return el;
}

export function playBackground(url, volume = 0.4) {
  bgUrl = url || activeTheme.musicUrl || DEFAULT_AUDIO_THEME.musicUrl;
  bgVolume = volume;
  if (!bgUrl) {
    startGeneratedBackground(volume);
    return;
  }
  if (bgEl && bgEl.src === bgUrl) {
    bgEl.volume = volume;
    bgEl.muted = muted;
    resumeBackground();
    return;
  }
  if (bgEl) { bgEl.pause(); bgEl = null; }
  const el = getEl(bgUrl);
  if (!el) return;
  el.loop = true;
  el.volume = volume;
  el.muted = muted;
  el.onerror = () => startGeneratedBackground(bgVolume);
  bgEl = el;
  resumeBackground();
}

export function resumeBackground() {
  if (!bgEl && bgUrl) {
    bgEl = getEl(bgUrl);
    if (bgEl) {
      bgEl.loop = true;
      bgEl.volume = bgVolume;
      bgEl.muted = muted;
    }
  }
  if (!bgEl || muted) return;
  try {
    const playPromise = bgEl.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise
        .then(() => stopGeneratedBackground())
        .catch(() => startGeneratedBackground(bgVolume));
    } else {
      stopGeneratedBackground();
    }
  } catch (e) {
    startGeneratedBackground(bgVolume);
  }
}

export function pauseBackground() {
  stopGeneratedBackground();
  if (!bgEl) return;
  try { bgEl.pause(); } catch (e) {}
}

export function playOneShot(url, volume = 0.8, fallback) {
  if (muted) return;
  if (!url) { if (fallback) fallback(); return; }
  const el = getEl(url);
  if (!el) { if (fallback) fallback(); return; }
  try {
    el.currentTime = 0;
    el.volume = volume;
    const playPromise = el.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => { if (fallback) fallback(); });
    }
  } catch (e) {
    if (fallback) fallback();
  }
}

export function tick(highPitch = false) {
  const fallback = () => synthTick(highPitch);
  const url = activeTheme.countdownSfxUrl || DEFAULT_AUDIO_THEME.countdownSfxUrl;
  if (url) {
    playOneShot(url, highPitch ? 0.42 : 0.25, fallback);
    return;
  }
  fallback();
}

export function playBetTap() {
  const fallback = () => synthBetTap();
  const url = activeTheme.betTapSfxUrl || DEFAULT_AUDIO_THEME.betTapSfxUrl;
  if (url) {
    playOneShot(url, 0.55, fallback);
    return;
  }
  fallback();
}

function synthBetTap() {
  if (muted) return;
  const c = ensureCtx();
  if (!c) return;
  const now = c.currentTime;
  const master = c.createGain();
  master.gain.setValueAtTime(0.0001, now);
  master.gain.linearRampToValueAtTime(0.22, now + 0.012);
  master.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
  master.connect(c.destination);

  [880, 1320].forEach((frequency, index) => {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = index === 0 ? 'triangle' : 'sine';
    osc.frequency.setValueAtTime(frequency, now);
    osc.frequency.exponentialRampToValueAtTime(frequency * 1.45, now + 0.08);
    gain.gain.value = index === 0 ? 0.75 : 0.35;
    osc.connect(gain).connect(master);
    osc.start(now + index * 0.018);
    osc.stop(now + 0.16 + index * 0.018);
  });
}

function synthTick(highPitch = false) {
  if (muted) return;
  const c = ensureCtx();
  if (!c) return;
  const now = c.currentTime;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'square';
  osc.frequency.value = highPitch ? 1100 : 700;
  gain.gain.setValueAtTime(0.18, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
  osc.connect(gain).connect(c.destination);
  osc.start(now);
  osc.stop(now + 0.1);
}

function startGeneratedBackground(volume = 0.25) {
  if (muted || bgSynth) return;
  const c = ensureCtx();
  if (!c) return;
  if (c.state === 'suspended') c.resume().catch(() => {});

  const master = c.createGain();
  master.gain.value = Math.max(0.01, Math.min(0.18, volume * 0.35));
  master.connect(c.destination);
  const notes = [261.63, 329.63, 392.0, 523.25, 392.0, 329.63, 293.66, 392.0];
  let index = 0;
  const playNote = () => {
    if (!bgSynth || muted) return;
    const now = c.currentTime;
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = index % 4 === 0 ? 'triangle' : 'sine';
    osc.frequency.value = notes[index % notes.length];
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.linearRampToValueAtTime(0.28, now + 0.035);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.42);
    osc.connect(gain).connect(master);
    osc.start(now);
    osc.stop(now + 0.45);
    index += 1;
  };
  bgSynth = { master, timer: setInterval(playNote, 420) };
  playNote();
}

function stopGeneratedBackground() {
  if (!bgSynth) return;
  clearInterval(bgSynth.timer);
  try { bgSynth.master.disconnect(); } catch (e) {}
  bgSynth = null;
}

export function chime(success = true) {
  if (muted) return;
  const c = ensureCtx();
  if (!c) return;
  const now = c.currentTime;
  const freqs = success ? [523.25, 659.25, 783.99, 1046.5] : [392, 311.13, 246.94];
  freqs.forEach((f, i) => {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'sine';
    osc.frequency.value = f;
    const t = now + i * 0.08;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
    osc.connect(gain).connect(c.destination);
    osc.start(t);
    osc.stop(t + 0.42);
  });
}

/**
 * Short mechanical "click" — emitted each time a gondola crosses the top
 * marker during a spin. Combine the rapid stream and you get a satisfying
 * ratcheting / clacking sound that conveys angular speed.
 */
export function ratchet() {
  if (muted) return;
  const c = ensureCtx();
  if (!c) return;
  const now = c.currentTime;
  // Short noise burst via filtered white noise
  const buffer = c.createBuffer(1, Math.floor(c.sampleRate * 0.04), c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  }
  const src = c.createBufferSource();
  src.buffer = buffer;
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 2200;
  filter.Q.value = 6;
  const gain = c.createGain();
  gain.gain.setValueAtTime(0.22, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
  src.connect(filter).connect(gain).connect(c.destination);
  src.start(now);
  src.stop(now + 0.06);
}

let whooshNodes = null;

/**
 * Continuous "whoosh" while the wheel is spinning. Filtered noise that
 * pitches up at start and down at end, giving an audible cue of wheel
 * acceleration and deceleration.
 */
export function whooshStart(durationMs = 4500) {
  if (muted) return;
  const c = ensureCtx();
  if (!c) return;
  whooshStop();
  const bufLen = Math.floor(c.sampleRate * 1.2);
  const buffer = c.createBuffer(1, bufLen, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufLen; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buffer;
  src.loop = true;
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.Q.value = 0.9;
  const gain = c.createGain();
  src.connect(filter).connect(gain).connect(c.destination);

  const now = c.currentTime;
  const dur = durationMs / 1000;
  // Frequency ramp: 400Hz → 1400Hz @ 60% → 300Hz at end
  filter.frequency.setValueAtTime(400, now);
  filter.frequency.linearRampToValueAtTime(1400, now + dur * 0.55);
  filter.frequency.linearRampToValueAtTime(280, now + dur);
  // Volume envelope: fade-in / sustain / fade-out
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.linearRampToValueAtTime(0.18, now + 0.18);
  gain.gain.linearRampToValueAtTime(0.16, now + dur * 0.7);
  gain.gain.linearRampToValueAtTime(0.0001, now + dur);

  src.start(now);
  whooshNodes = { src, gain, stopAt: now + dur + 0.05 };
  src.stop(now + dur + 0.1);
}

export function whooshStop() {
  if (!whooshNodes) return;
  const c = ensureCtx();
  try {
    if (c) {
      whooshNodes.gain.gain.cancelScheduledValues(c.currentTime);
      whooshNodes.gain.gain.setValueAtTime(whooshNodes.gain.gain.value, c.currentTime);
      whooshNodes.gain.gain.linearRampToValueAtTime(0.0001, c.currentTime + 0.12);
    }
    whooshNodes.src.stop(c ? c.currentTime + 0.15 : 0);
  } catch (e) {/* ignore */}
  whooshNodes = null;
}
